/**
 * src/modules/dashboard/componentes/npsconcorrentes.js
 */

import React from 'react';
import './npsconcorrentes.scss';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush
} from 'recharts';
import { IcoArchive } from '../../../components';

// Tooltip para NPS Concorrentes (sem %)
const CustomTooltip = ({ active, payload, label, labels, dadonaodisponivel }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="custom-tooltip">
      <p className="label">{label}</p>
      {payload.map(({ name, value, color, payload: rawData }, i) => {
        const labelItem = labels.find(l => l.type === name);
        const entry = rawData?.item?.[i];
        
        // Verifica se o dado não está disponível
        if (entry?.info === -1) {
          return (
            <p key={`tooltip-${name}`} className="desc">
              <span style={{ backgroundColor: color }}></span>
              {`${labelItem?.name || name}: ${dadonaodisponivel}`}
            </p>
          );
        }
        
        // Para valores numéricos (incluindo 0), mostra sem %
        const displayValue = value === 0 ? '0' : value;
        return (
          <p key={`tooltip-${name}`} className="desc">
            <span style={{ backgroundColor: color }}></span>
            {`${labelItem?.name || name}: ${displayValue}`}
          </p>
        );
      })}
    </div>
  );
};

const RenderLegend = ({ labels }) => (
  <div className="custom-legend legend-center">
    {labels.map((label, i) => (
      <div key={`legend-${i}`}>
        <span style={{ backgroundColor: label.color }}></span>
        {label.name}
      </div>
    ))}
  </div>
);

// CustomLabel para NPS Concorrentes (sem %, com suporte a negativos)
const CustomLabel = ({ x, y, width, value }) => {
  if (value === null || value === undefined) return null;
  const displayValue = value === 0 ? '0' : value;
  return (
    <text
      x={x + width / 2}
      y={value < 0 ? y + 16 : y}
      dy={-4}
      textAnchor="middle"
      fill="#6c7293"
      fontSize="11px"
    >
      {displayValue}
    </text>
  );
};

// Opções para NPS Concorrentes (-100 a 100)
const graficoOptions = {
  BarChart: { margin: { top: 40, right: 40, left: 20, bottom: 60 } },
  CartesianGrid: { stroke: '#e6e6e6', vertical: false },
  Brush: { dataKey: 'name', height: 30, stroke: '#dc143c', startIndex: 0, endIndex: 5 },
  XAxis: { dataKey: 'name', fontSize: '12px', stroke: '#6c7293', axisLine: false, tickLine: false },
  YAxis: { 
    fontSize: '12px', 
    stroke: '#6c7293', 
    axisLine: false, 
    tickLine: false,
    domain: [-100, 100],
    ticks: [-100, -50, 0, 50, 100]
  },
  Bar: { radius: [6, 6, 6, 6] }
};

export default function NpsConcorrentes({
  nls = {},
  componentNls = {},
  filtros = [],
  payloadData = {},
  totaldata
}) {
  // Estrutura do payloadData para npsconcorrentes
  const data = payloadData?.data || [];
  const labels = payloadData?.labels || [];
  const analise = payloadData?.analise || [];
  const dadonaodisponivel = componentNls?.dadonaodisponivel || 'No available data';

  const transformData = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) return [];
    return rawData.map(item => {
      const result = { name: item.name, item: item.item };
      item.item.forEach(entry => {
        result[entry.chave] = parseFloat(entry.valor);
      });
      return result;
    });
  };

  const chartData = transformData(data);
  const hasData = chartData.length > 0 && labels.length > 0;

  return (
    <>
    <section className="box-content-like npsconcorrentes-wrapper">
      {filtros.length > 0 && (
        <div className="feedback-filtros-aplicados">
          <p className="filtros-label">{nls?.filtrosAplicados || 'Filtros aplicados'}:</p>
          <ul className="filtros-lista">
            {filtros.map((filtro, idx) => (
              <li key={idx} className="filtro-item">
                <strong>{filtro.nomedofiltro}:</strong> {filtro.valordofiltro}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="npsconcorrentes-grafico">
        {hasData ? (
          <ResponsiveContainer width="100%" height={500}>
            <BarChart data={chartData} margin={graficoOptions.BarChart.margin}>
              <CartesianGrid stroke={graficoOptions.CartesianGrid.stroke} vertical={graficoOptions.CartesianGrid.vertical} />
              {chartData.length > 4 && (
                <Brush
                  dataKey={graficoOptions.Brush.dataKey}
                  height={graficoOptions.Brush.height}
                  stroke={graficoOptions.Brush.stroke}
                  startIndex={graficoOptions.Brush.startIndex}
                  endIndex={Math.min(graficoOptions.Brush.endIndex, chartData.length - 1)}
                />
              )}
              <XAxis {...graficoOptions.XAxis} />
              <YAxis {...graficoOptions.YAxis} />
              <Tooltip content={<CustomTooltip labels={labels} dadonaodisponivel={dadonaodisponivel} />} />
              <Legend content={<RenderLegend labels={labels} />} />
              {labels.map((label) => (
                <Bar
                  key={`bar-${label.type}`}
                  dataKey={label.type}
                  fill={label.color}
                  radius={graficoOptions.Bar.radius}
                  label={<CustomLabel />}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="npsconcorrentes-no-data">
            {componentNls?.semdadosparaexibir || nls?.semdadosparaexibir || 'No data'}
          </div>
        )}
      </div>

      {analise.length > 0 && (
        <div className="npsconcorrentes-analise">
          <h3>
            <IcoArchive style={{ width: 20, height: 20, marginRight: 8 }} />
            {componentNls?.analise || 'Analysis'}
          </h3>
          <div className="analise-content">
            {analise.map((item, idx) => (
              <div key={idx} className="analise-item">
                <strong>{item.topico}:</strong> {item.texto}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
    {totaldata !== null && totaldata !== undefined && (
      <div className="feedback-totaldata-outside">
        {nls?.numerodefeedbacks}: <strong>{totaldata}</strong>
      </div>
    )}
  </>
  );
}


/*
JSON INPUT:


{
      "nls": {
        "analise": "Analysis",
        "dadonaodisponivel": "No available data",
        "semdadosparaexibir": "No data"
      },
      "label": "Nps Concorrentes Empresas",
      "scope": {
        "periodo": {
          "fim": "2025-09-30",
          "ini": "2025-09-01"
        },
        "projetoId": 46
      },
      "params": {
        "idioma": 1,
        "empresaId": 225,
        "comparacao": "false"
      },
      "Filtros": [
        {
          "nomedofiltro": "Empresa",
          "valordofiltro": "McDonald´s"
        }
      ],
      "ordinal": 17,
      "topicKey": "npsconcorrentes"
    }


*/